import React, { useEffect, useMemo } from 'react';
import { Flex, Text } from '@fluentui/react-northstar';
import { MultiTypeaheadField, TypeaheadField } from '../table/TypeaheadField';
import { CategoryOption, CategoryRecord, buildCategoryOptions, withHeldOptions } from '../table/inlineCategoryOptions';
import { Item, NestedDropdownProps } from './NestedDropdown';
import { RequiredAsterisk } from '../FieldErrorText';

/**
 * The ticket list's typable picker with NestedDropdown's props, so a form's field swaps over without its handlers
 * changing - they are given the same item objects NestedDropdown gave them. Typing filters anywhere in an option's
 * full path, the best match is lit, and a value is taken when it is picked (TypeaheadField, liveChange off). A
 * several-pick dropdown (isMulti) is the chip box (MultiTypeaheadField): `defaultValue` holds the picked ids, comma
 * separated, and onMultiChange gets them back - as NestedDropdown's. Built to stand in for NestedDropdown everywhere.
 */
export const TypeaheadPicker = (props: NestedDropdownProps & {
  /** The screen reader's name for a picker whose label is drawn outside it (Bulk Edit's checkbox labels). */
  ariaLabel?: string;
  /** Values `items` leaves out (archived or deleted) that a several-pick box may still hold - shown as its chips. */
  heldItems?: Item[];
}) => {
  const { label, ariaLabel, placeholder, items = [], dataItem, defaultValue, onChange, disabled, showClearBtn, error, isMulti, onMultiChange, isRequired, triggerDefaultValueSet, heldItems } = props;

  const options = useMemo<CategoryOption[]>(() => {
    const record = (item: Item): CategoryRecord => ({
      Id: item.id,
      Value: item.value,
      ParentId: item.parentId,
      Position: item.position,
      IsArchived: (item as Item & { IsArchived?: boolean }).IsArchived,
      IsDeleted: (item as Item & { IsDeleted?: boolean }).IsDeleted
    });
    const built = buildCategoryOptions(items.map(record));
    // A value no longer offered (archived since it was set) still shows as the field's value, as NestedDropdown did -
    // the lists the forms pass leave archived values out, so it is named from dataItem / heldItems.
    const held = [...(heldItems ?? []), ...(dataItem?.id != null && dataItem.value ? [dataItem] : [])];
    const picked = isMulti ? (defaultValue ?? '').split(',').map(id => Number(id.trim())) : [dataItem?.id];
    return withHeldOptions(built, [...items, ...held].map(record), picked);
  }, [items, dataItem?.id, dataItem?.value, heldItems, isMulti, defaultValue]);

  const valueId = dataItem !== undefined
    ? dataItem?.id ?? null
    : items.find(item => item.value === defaultValue)?.id ?? null;

  /** A several-pick dropdown's ids, in the order given, those the options know. */
  const multiIds = useMemo(() => (isMulti ? (defaultValue ?? '').split(',') : [])
    .map(id => Number(id.trim())).filter(id => id && options.some(option => option.id === id)), [isMulti, defaultValue, options]);

  // NestedDropdown's mount behaviour, so a form relying on it gets the same calls: with no dataItem, a defaultValue
  // is reported as the choice; with triggerDefaultValueSet, the dataItem is (several-pick: the picked ids are).
  useEffect(() => {
    if (isMulti) {
      if (triggerDefaultValueSet) onMultiChange?.(multiIds);
      return;
    }
    if (dataItem !== undefined) {
      if (triggerDefaultValueSet) onChange(items.find(item => item.id === dataItem.id));
    } else if (defaultValue) {
      onChange(items.find(item => item.value === defaultValue));
    }
  }, [dataItem]);

  return (
    <div>
      {label && (
        <Flex>
          <Text content={label} style={{ display: 'block' }} />
          {isRequired && <RequiredAsterisk />}
        </Flex>
      )}
      {isMulti ? (
        <MultiTypeaheadField
          label={label ?? ariaLabel ?? placeholder ?? ''}
          hideLabel
          listFitsBox
          options={options}
          valueIds={multiIds}
          onChange={ids => onMultiChange?.(ids)}
          placeholder={placeholder}
          disabled={disabled}
        />
      ) : <TypeaheadField
        label={label ?? ariaLabel ?? placeholder ?? ''}
        hideLabel
        listFitsBox
        options={options}
        valueId={valueId}
        onChange={id => onChange(id == null ? null : items.find(item => item.id === id))}
        placeholder={placeholder}
        clearable={showClearBtn}
        disabled={disabled}
        error={error}
        liveChange={false}
      />}
    </div>
  );
};
